import { StudentBirthday } from 'Src/components/StudentBirthday/StudentBirthday';
import {TournamentAvailabilityIcon} from 'Src/components/TournamentAvailabilityIcon/TournamentAvailabilityIcon';
import * as React from 'react';
import { useState } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent } from 'Src/models/event';
import { SchoolEventIndividualData } from 'Src/models/event';
import { CELL_ACTION_ICON } from 'Src/consts/cell';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { DeleteIcon } from 'Src/components/SvgIcons/DeleteIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './EventParticipants.scss';
import { TournamentSelectionSources } from 'Src/components/TournamentSelectionSources/TournamentSelectionSources';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  onClick: (
    data: {
      currentEvent: SchoolEvent;
      currentParticipant: SchoolEventIndividualData;
    },
    cell: string
  ) => void;
  showPositions?: boolean;
  maxVisible?: number;
  modern?: boolean;
  readOnly?: boolean;
}

export function EventParticipants(props: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { event, onClick, user, showPositions, maxVisible = 3, modern = false, readOnly = false } = props;
  const { individualsData = [], teamsData = [], players, embeddedTeams = [] } = event;
  const schoolIds = propz.get(user, ['activeSchool', 'schoolIds'], []);

  const rawPositions = (event as any)?.sport?.field?.positions || (event as any)?.sport?.positions || [];

  const positionOrder = new Map<string, number>(
    rawPositions.map((p: any, idx: number) => [String(p.id || p._id), idx])
  );
  const positionLabel = new Map<string, string>(
    rawPositions.map((p: any) => [String(p.id || p._id), p.shortName || p.name])
  );

  const playerIndex = new Map<string, { positionId: string; isCaptain: boolean; isSub: boolean }>();
  for (const pl of (event.players || []) as any[]) {
    const key = `${String(pl.userId || '')}|${String(pl.permissionId || '')}`;
    playerIndex.set(key, {
      positionId: pl.positionId ? String(pl.positionId) : '',
      isCaptain: !!pl.isCaptain,
      isSub: !!pl.sub
    });
  }

  const isRelay = propz.get(event, ['sport', 'isRelay'], false);
  let participantsFromCurrentSchool = [];

  if (isRelay) {
    const ownTeamIds = new Set(embeddedTeams.filter(team => String(team.schoolId) === user.activeSchoolId).map(team => String(team._id)));
    participantsFromCurrentSchool = teamsData.filter(team => ownTeamIds.has(String(team.id))).reduce((rows, team) => rows.concat(team.players), [] as any[]);
  } else {
    participantsFromCurrentSchool = individualsData.filter(item => String(item.schoolId) === user.activeSchoolId || schoolIds.map(String).includes(String(item.schoolId)));
  }

  const isParticipantsExist = participantsFromCurrentSchool.length > 0;

  if (isParticipantsExist) {
    const sorted = [...participantsFromCurrentSchool].sort((a, b) => {
      const keyA = `${String(a.userId || '')}|${String(a.permissionId || '')}`;
      const keyB = `${String(b.userId || '')}|${String(b.permissionId || '')}`;

      const pa = playerIndex.get(keyA);
      const pb = playerIndex.get(keyB);

      const hasPosA = pa?.positionId ? 0 : 1;
      const hasPosB = pb?.positionId ? 0 : 1;
      if (hasPosA !== hasPosB) return hasPosA - hasPosB;

      const orderA = pa?.positionId ? positionOrder.get(pa.positionId) ?? 9999 : 9999;
      const orderB = pb?.positionId ? positionOrder.get(pb.positionId) ?? 9999 : 9999;
      if (orderA !== orderB) return orderA - orderB;

      const labelA = pa?.positionId ? positionLabel.get(pa.positionId) || '' : '';
      const labelB = pb?.positionId ? positionLabel.get(pb.positionId) || '' : '';
      if (labelA !== labelB) return labelA.localeCompare(labelB);

      return getFullName(a).localeCompare(getFullName(b));
    });

    const limit = typeof maxVisible === 'number' ? maxVisible : 3;
    const showAll = limit <= 0 || limit >= sorted.length || isOpen;
    const participantsData = showAll ? sorted : sorted.slice(0, limit);

    return (
      <td className={modern ? 'hltParticipantEvents__participants' : 'mPreWrap'} onClick={modern ? e => e.stopPropagation() : undefined}>
        {modern && <small className="hltParticipantEvents__participantCount">{sorted.length} student{sorted.length === 1 ? '' : 's'}</small>}
        {participantsData.map(participant => {
          const participantName = getFullName(participant);
          const { id } = participant;

          const key = `${String(participant.userId || '')}|${String(participant.permissionId || '')}`;
          const idx = playerIndex.get(key) || { positionId: '', isCaptain: false, isSub: false };
          const posLabel = showPositions && idx.positionId ? positionLabel.get(idx.positionId) || idx.positionId : null;

          const data = {
            currentEvent: event,
            currentParticipant: participant
          };

          return (
            <div key={id || key} className={modern ? 'hltParticipantEvents__student' : 'd-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded bg-light'}>
              <div className="d-flex align-items-center">
                <span className="mr-2"><span className="hltParticipantEvents__identity"><TournamentAvailabilityIcon player={participant} />{participantName}</span>
                  <span className="hltParticipantEvents__school">{participant.schoolName || 'School not recorded'}</span>
                  <StudentBirthday birthday={(participant as any).birthday} labelled /><TournamentSelectionSources sources={(participant as any).selectionSources} />
                  {(participant as any).tournamentAvailability === 'NOT_AVAILABLE' && <small className="text-danger">Not available · replacement needed</small>}</span>
                {posLabel && <span className="badge badge-info mr-2">{posLabel}</span>}
                {idx.isCaptain && <span className="badge badge-warning mr-2">C</span>}
                {idx.isSub && <span className="badge badge-secondary">Sub</span>}
              </div>
              {!readOnly && (modern ? <button type="button" className="hltParticipantEvents__remove" aria-label={`Remove ${participantName} from event`} title={`Remove ${participantName} from event`} onClick={() => onClick(data, CELL_ACTION_ICON.REMOVE_FROM_EVENT)}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M6 18 18 6" /></svg>
              </button> : <DeleteIcon onClick={() => onClick(data, CELL_ACTION_ICON.REMOVE_FROM_EVENT)} />)}
            </div>
          );
        })}

        {limit > 0 && sorted.length > limit && (
          <button type="button" className={modern ? 'hltParticipantEvents__showMore' : 'bEventParticipantsShowMoreButton'} aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}>
            {modern ? (isOpen ? 'Show fewer' : `Show all ${sorted.length} students`) : (isOpen ? 'Hide' : 'Show All')}
            <FontAwesomeIcon
              color="#828282"
              className={isOpen ? 'eEventParticipantsShowMoreButtonIcon' : 'mEventParticipantsShowMoreButtonIconActive'}
              icon="arrow-right"
            />
          </button>
        )}
      </td>
    );
  }

  return <td className={modern ? 'hltParticipantEvents__participants' : 'mPreWrap'}>{modern && <span className="hltParticipantEvents__empty">No participants</span>}</td>;
}


